import { useCallback, useMemo } from 'react';
import filter from 'lodash/filter';
import first from 'lodash/first';
import concat from 'lodash/concat';
import isEmpty from 'lodash/isEmpty';
import trim from 'lodash/trim';
import compact from 'lodash/compact';
import reduce from 'lodash/reduce';
import find from 'lodash/find';
import uniq from 'lodash/uniq';
import map from 'lodash/map';
import pick from 'lodash/pick';

import type {
  ChangeMaterialsFiltersFunc,
  FetchMaterialsFilters
} from '../../../../../../../../materials/materialsTypes';
import { FetchMaterialsFilterFields } from '../../../../../../../../materials/materialsTypes';
import type {
  CategoryID,
  CategoryLocalizedName
} from '../../../../../../../../categories/categoriesTypes';

import { FetchMaterialCategoriesQueryResponse } from '../../../../../../../../materials/queries/fetchMaterialCategories.query';

import { useCacheMaterialsAggregations } from '../../../../../../../../materials/hooks/useCacheMaterialsAggregations';
import { useThreeDStockCategories } from '../../../../../../../hooks/useThreeDStockCategories';

import { getChangeMaterialsFiltersCategoriesData } from '../../../../../../../../materials/utils/getChangeMaterialsFiltersCategoriesData';

import { getAllParentsCategories } from '../../../../../../../../categories/utils/getAllParentsCategories';

import { StockTabs } from '../../../../../../../stockConstants';

interface MaterialsFilterCategoriesOptions {
  materialsFilters: FetchMaterialsFilters;
  defaultMaterialCategoryIds?: CategoryID[];
  changeMaterialsFilters: ChangeMaterialsFiltersFunc;
  checkedCategories: { id: CategoryID; localizedName: CategoryLocalizedName }[];
  setCheckedCategories: (
    checkedCategories: {
      id: CategoryID;
      localizedName: CategoryLocalizedName;
    }[]
  ) => void;
  changeCategory: (
    changeFilters?: FetchMaterialsFilters,
    removeFilters?: string[]
  ) => void;
}

function useMaterialsFilterCategories({
  materialsFilters,
  defaultMaterialCategoryIds,
  changeMaterialsFilters,
  changeCategory,
  checkedCategories,
  setCheckedCategories
}: MaterialsFilterCategoriesOptions) {
  const checkedCategoryIds = map(checkedCategories, 'id');

  const materialsFiltersForCategories = useMemo<FetchMaterialsFilters>(() => {
    const filtersWithoutCategories = pick(materialsFilters, [
      FetchMaterialsFilterFields.TEAM_IDS,
      FetchMaterialsFilterFields.CLIENT_IDS,
      FetchMaterialsFilterFields.NDA,
      FetchMaterialsFilterFields.FAVORITE,
      FetchMaterialsFilterFields.BLOCKED,
      FetchMaterialsFilterFields.CURRENT_TEAM_FAVORITE,
      FetchMaterialsFilterFields.TEAM_ID_FAVORITE,
      FetchMaterialsFilterFields.NAME_SKU_TAGS
    ]);

    return {
      ...filtersWithoutCategories,
      materialCategoryIds: defaultMaterialCategoryIds
    };
  }, [defaultMaterialCategoryIds, materialsFilters]);

  const { materialsAggregations, materialsAggregationsFetched } =
    useCacheMaterialsAggregations({
      materialsFilters: materialsFiltersForCategories
    });

  const {
    categories: materialCategories,
    categoriesFetched: allMaterialCategoriesFetched,
    currentCategory
  } = useThreeDStockCategories({
    tab: StockTabs.MATERIALS,
    categoryIds: defaultMaterialCategoryIds
  });

  const materialsAggregationsCategories = useMemo<
    FetchMaterialCategoriesQueryResponse[]
  >(() => {
    if (isEmpty(materialsAggregations)) {
      return materialCategories;
    }

    const availableCategories = concat(materialsAggregations?.categories);

    return materialCategories?.filter((category) =>
      availableCategories?.some(
        (availableCategory) => availableCategory.id === category.id
      )
    );
  }, [materialCategories, materialsAggregations]);

  const listCategories = useMemo<FetchMaterialCategoriesQueryResponse[]>(() => {
    const defaultCurrentCategoryId = first(defaultMaterialCategoryIds);

    return getAllParentsCategories(
      materialCategories,
      materialsAggregationsCategories,
      defaultCurrentCategoryId
    )?.sort((a, b) => {
      return (
        trim(a.localizedName)?.localeCompare(trim(b.localizedName), undefined, {
          numeric: true,
          sensitivity: 'base'
        }) || 0
      );
    });
  }, [
    defaultMaterialCategoryIds,
    materialCategories,
    materialsAggregationsCategories
  ]);

  const handleCheckedCategory = useCallback<(categoryId: CategoryID) => void>(
    (categoryId) => {
      const isChecked = checkedCategoryIds?.includes(categoryId);

      const newCheckedCategoryIds = isChecked
        ? checkedCategoryIds.filter((id) => id !== categoryId)
        : [...checkedCategoryIds, categoryId];

      const newCheckedCategories = filter(materialCategories, (category) =>
        newCheckedCategoryIds.includes(category.id)
      );

      setCheckedCategories(newCheckedCategories);

      if (isEmpty(newCheckedCategoryIds)) {
        const { changeFilters, resetFilters } =
          getChangeMaterialsFiltersCategoriesData({
            materialCategory: currentCategory,
            categories: materialCategories
          });

        return changeMaterialsFilters(changeFilters, resetFilters);
      }

      const combinedFilters = reduce(
        newCheckedCategoryIds,
        (result, categoryId) => {
          const category = find(materialCategories, ['id', categoryId]);

          const { changeFilters } = getChangeMaterialsFiltersCategoriesData({
            materialCategory: category,
            categories: materialCategories
          });

          return {
            [FetchMaterialsFilterFields.MATERIAL_PARENT_CATEGORY_IDS]: uniq(
              compact(
                concat(
                  result[
                    FetchMaterialsFilterFields.MATERIAL_PARENT_CATEGORY_IDS
                  ],
                  changeFilters[
                    FetchMaterialsFilterFields.MATERIAL_PARENT_CATEGORY_IDS
                  ]
                )
              )
            ),
            [FetchMaterialsFilterFields.MATERIAL_CATEGORY_IDS]: uniq(
              compact(
                concat(
                  result[FetchMaterialsFilterFields.MATERIAL_CATEGORY_IDS],
                  changeFilters[
                    FetchMaterialsFilterFields.MATERIAL_CATEGORY_IDS
                  ]
                )
              )
            )
          };
        },
        {
          [FetchMaterialsFilterFields.MATERIAL_PARENT_CATEGORY_IDS]: [],
          [FetchMaterialsFilterFields.MATERIAL_CATEGORY_IDS]: []
        }
      );

      const resetFilters = compact([
        !isEmpty(
          combinedFilters[FetchMaterialsFilterFields.MATERIAL_CATEGORY_IDS]
        )
          ? null
          : FetchMaterialsFilterFields.MATERIAL_CATEGORY_IDS,
        !isEmpty(
          combinedFilters[
            FetchMaterialsFilterFields.MATERIAL_PARENT_CATEGORY_IDS
          ]
        )
          ? null
          : FetchMaterialsFilterFields.MATERIAL_PARENT_CATEGORY_IDS
      ]);

      changeMaterialsFilters(combinedFilters, resetFilters);
    },
    [
      changeMaterialsFilters,
      checkedCategoryIds,
      currentCategory,
      materialCategories,
      setCheckedCategories
    ]
  );

  const handleChangeCategory = useCallback<(categoryId: CategoryID) => void>(
    (categoryId) => {
      const materialCategory = find(materialCategories, ['id', categoryId]);

      const { changeFilters, resetFilters } =
        getChangeMaterialsFiltersCategoriesData({
          materialCategory,
          categories: materialCategories
        });

      changeCategory(changeFilters, resetFilters);
    },
    [changeCategory, materialCategories]
  );

  return {
    listCategories,
    listCategoriesFetched:
      allMaterialCategoriesFetched && materialsAggregationsFetched,
    checkedCategoryIds,
    handleCheckedCategory,
    handleChangeCategory
  };
}

export default useMaterialsFilterCategories;
